import { createContext, useContext, useMemo } from 'react';

import { Logout } from './auth-hooks';
import { TimeoutProvider } from './timeout-context';
import { AuthenticationResponse, PostLogin } from './types';
import { useAuth } from './use-auth';
import { GetOrganisationSettings } from './use-get-organisation-auth-settings';

type AuthContextValue = {
  authenticatedUser?: AuthenticationResponse;
  authError?: string;
  authSystemId?: string;
  getOrgSettings: GetOrganisationSettings;
  handleCallback: () => Promise<void>;
  initiateChangePassword: () => Promise<void>;
  initiateHostedLogin: (redirect?: string) => Promise<void>;
  isAuthenticated: boolean;
  isLoading: boolean;
  logout: Logout;
  organisation?: string;
  clientId?: string;
  bulkReferralEnabled?: boolean;
  aiChatEnabled?: boolean;
  postLogin: PostLogin;
  apiUrl: string;
};

const AuthContext = createContext<AuthContextValue>({
  apiUrl: '',
  getOrgSettings: () => Promise.resolve({ clientId: undefined, defaultClientId: false }),
  handleCallback: () => Promise.resolve(),
  initiateChangePassword: () => Promise.resolve(),
  initiateHostedLogin: () => Promise.resolve(),
  isAuthenticated: false,
  isLoading: false,
  logout: () => Promise.resolve(undefined),
  postLogin: async () => undefined,
});

type AuthProviderProps = {
  readonly apiUrl: string;
  readonly children: React.ReactElement;
  readonly clientId?: string;
  readonly cognitoDomain: string;
  readonly userTimeoutInMinutes?: number;
};

const AuthProvider = ({ apiUrl, children, clientId, cognitoDomain, userTimeoutInMinutes }: AuthProviderProps) => {
  return (
    <TimeoutProvider userTimeoutInMinutes={userTimeoutInMinutes}>
      <AuthProviderInner apiUrl={apiUrl} clientId={clientId} cognitoDomain={cognitoDomain}>
        {children}
      </AuthProviderInner>
    </TimeoutProvider>
  );
};

const AuthProviderInner = ({ apiUrl, children, clientId, cognitoDomain }: AuthProviderProps) => {
  const authData = useAuth({
    apiUrl,
    clientId,
    cognitoDomain,
  });

  const authContext = useMemo(
    () => ({
      aiChatEnabled: authData.authenticatedUser?.aiChatEnabled,
      apiUrl,
      authError: authData.authError,
      authSystemId: authData.authSystemId,
      authenticatedUser: authData.authenticatedUser,
      bulkReferralEnabled: authData.authenticatedUser?.bulkReferralEnabled,
      clientId,
      getOrgSettings: authData.getOrgSettings,
      handleCallback: authData.handleCallback,
      initiateChangePassword: authData.initiateChangePassword,
      initiateHostedLogin: authData.initiateHostedLogin,
      isAuthenticated: authData.isAuthenticated,
      isLoading: authData.isLoading,
      logout: authData.logout,
      organisation: authData.organisation,
      postLogin: authData.postLogin,
    }),
    [authData, clientId, apiUrl]
  );

  return <AuthContext.Provider value={authContext}>{children}</AuthContext.Provider>;
};

const useAuthentication = () => useContext(AuthContext);

export { AuthProvider, useAuthentication };
